/**
 * Hook de sincronización automática (ola 2 · O2.5, propuesta 17.2).
 *
 * Montarlo UNA vez en el árbol siempre presente dentro de <BrowserRouter>
 * (components/mobile/MobileAppWrapper.jsx): al abrir la app (tras un idle
 * callback) y al recuperar la red (evento `online`, con 3 s de espera) envía lo
 * pendiente en silencio. El servicio ya comprueba sesión y conexión y usa un
 * mutex, así que no importa que OfflineTestPage lo dispare también.
 */
import { useEffect } from 'react';
import { syncPendingOffline } from '../../services/offlineSync';

export const OFFLINE_SYNC_ENABLED = process.env.REACT_APP_OFFLINE_ENABLED === 'true';

export function useOfflineSync({ enabled = OFFLINE_SYNC_ENABLED, onlineDelayMs = 3000 } = {}) {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
    const cancelIdle = window.cancelIdleCallback || clearTimeout;
    const idleId = idle(() => { syncPendingOffline({ silent: true }); });
    let timer = null;
    const onOnline = () => {
      clearTimeout(timer);
      timer = setTimeout(() => { syncPendingOffline({ silent: true }); }, onlineDelayMs);
    };
    window.addEventListener('online', onOnline);
    return () => {
      cancelIdle(idleId);
      clearTimeout(timer);
      window.removeEventListener('online', onOnline);
    };
  }, [enabled, onlineDelayMs]);
}

export default useOfflineSync;
